import { useAppStore } from "@/store";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { MDSToggle } from "@/mds/components/forms";
import { BoldedList } from "@/domains/templates/generateTemplateSharingText";
import {
  MDSBanner,
  MDSBannerKind,
  MDSBannerSlotName,
  MDSText,
  MDSTextWeight,
  MDSIconKind,
  MDSShortcutKey,
  MDSShortcutKeyCharacter,
} from "@/mds";

interface TemplatesEditorBannerProps {
  template: TemplateObservable;
}

export const TemplatesEditorBanner = observer<TemplatesEditorBannerProps>(
  function TemplatesEditorBanner({ template }) {
    const { store } = useAppStore();
    const targetScopes = template.modelData.target_scopes;

    const targetScopeCollectionIds = useMemo(
      () =>
        targetScopes
          .filter(scope => scope.scope_kind === "COLLECTION_SCOPE")
          .map(scope => scope.value.collection_id),
      [targetScopes]
    );
    const targetScopeSpaceAccountIds = useMemo(
      () =>
        targetScopes
          .filter(scope => scope.scope_kind === "SPACE_ACCOUNT_SCOPE")
          .map(scope => scope.value.space_account_id)
          .filter(id => id !== store.spaceAccounts.myPersonalSpaceAccountId),
      [targetScopes, store.spaceAccounts.myPersonalSpaceAccountId]
    );

    const targetScopeCollections = useMemo(
      () => store.collections.getMany(targetScopeCollectionIds),
      [store.collections, targetScopeCollectionIds]
    );
    const targetScopeContacts = useMemo(
      () => store.contacts.getManyBySpaceAccountId(targetScopeSpaceAccountIds),
      [store.contacts, targetScopeSpaceAccountIds]
    );
    const hasTargetScopes = targetScopeCollections.length > 0 || targetScopeContacts.length > 0;
    const isAutoSharingEnabled = template.isAutoSharingEnabled;
    const isDefaultCalendarEventTemplate = template.isDefaultCalendarEventTemplate;

    const { privateCollectionNames, sharedNames } = useMemo(() => {
      const privateCollectionNames = targetScopeCollections
        .filter(collection => collection.modelData.shared_at === null)
        .map(collection => collection.label);
      const sharedCollectionNames = targetScopeCollections
        .filter(collection => collection.modelData.shared_at !== null)
        .map(collection => collection.label);
      const contactEmails = targetScopeContacts.map(contact => contact?.profileEmailAddress);
      const sharedNames = [...sharedCollectionNames, ...contactEmails];
      return { privateCollectionNames, sharedNames };
    }, [targetScopeCollections, targetScopeContacts]);

    const sharingHeadingText = useMemo(() => {
      if (privateCollectionNames.length > 0 && sharedNames.length > 0) {
        return (
          <>
            Automatically add notes created with this template to{" "}
            <BoldedList items={privateCollectionNames} /> and share notes created with this template
            with <BoldedList items={sharedNames} />
          </>
        );
      }
      if (privateCollectionNames.length > 0) {
        return (
          <>
            Automatically add notes created with this template to{" "}
            <BoldedList items={privateCollectionNames} />
          </>
        );
      }
      if (sharedNames.length > 0) {
        return (
          <>
            Automatically share notes created with this template with{" "}
            <BoldedList items={sharedNames} />
          </>
        );
      }
      return (
        <>
          You&apos;re editing a template. Type{" "}
          <MDSShortcutKey character={MDSShortcutKeyCharacter.Slash} inline /> to insert date and
          name variables.
        </>
      );
    }, [privateCollectionNames, sharedNames]);

    const instructionsHeadingText = useMemo(() => {
      if (isDefaultCalendarEventTemplate) {
        return (
          <>
            You&apos;re editing the template that will be used by default for any note created from
            a calendar event. Type{" "}
            <MDSShortcutKey character={MDSShortcutKeyCharacter.Slash} inline /> to insert meeting
            variables.
          </>
        );
      }

      return (
        <>
          You&apos;re editing a template. Type{" "}
          <MDSShortcutKey character={MDSShortcutKeyCharacter.Slash} inline /> to insert date and
          name variables.
        </>
      );
    }, [isDefaultCalendarEventTemplate]);

    const handleAutoSharingSwitch = async (value: boolean) => {
      await template.update({ isAutoSharingEnabled: value });
    };

    const showSharingBanner = hasTargetScopes;
    const showInstructionsBanner = isDefaultCalendarEventTemplate || !hasTargetScopes;

    return (
      <>
        {showInstructionsBanner && (
          <MDSBanner
            kind={MDSBannerKind.Default}
            iconKind={MDSIconKind.InfoCircle}
            message={instructionsHeadingText}
          />
        )}
        {showSharingBanner && (
          <MDSBanner
            kind={isAutoSharingEnabled ? MDSBannerKind.Info : MDSBannerKind.Default}
            iconKind={MDSIconKind.InfoCircle}
            message={sharingHeadingText}
          >
            {hasTargetScopes && (
              <ToggleContainer slot={MDSBannerSlotName.Right}>
                <MDSText weight={MDSTextWeight.Smedium}>
                  {isAutoSharingEnabled ? "On" : "Off"}
                </MDSText>
                <MDSToggle
                  checked={isAutoSharingEnabled}
                  onChange={() => handleAutoSharingSwitch(!isAutoSharingEnabled)}
                />
              </ToggleContainer>
            )}
          </MDSBanner>
        )}
      </>
    );
  }
);

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;
